import * as React from 'react';
import { compose } from 'redux';
import { withLocalization, LProps } from '../../utils/wrappers';
import { CompetitionPledge } from '../../userApi';
import { Link } from 'react-router-dom';
import { makeActionSavingsPath, resolveCompetitionSlug } from '../../utils/routerHelper';
import {
  calculatePointsForNextStory, repeatPointsForPledge
} from '../../utils/userApiHelper';

interface Props {
  pledge: CompetitionPledge;
  completions: number;
  pointsEarned: number;
}

const PledgeStats = ({
  t, formatNumber, pledge, completions, pointsEarned
}: LProps<Props>) => {
  const pledgeCompletions = { [pledge.id]: completions };
  const nextPoints = calculatePointsForNextStory(pledgeCompletions, pledge);
  const hasRepeatScoring = pledge.repeats > 1;
  const isCcsf = resolveCompetitionSlug() === 'ccsf';
  // Legacy savingsWaterOz field stores gallons (see Action Savings methodology).
  const waterGallons = Number(pledge.savingsWaterOz || 0);

  return (
  <div className="cc__transparent_box">
    <div className="cc__stats__item">
      <div className="cc__stats__label">
        {hasRepeatScoring ? t('Point values') : t('Baseline Points')}
      </div>
      <div className="cc__lighttext">
        {hasRepeatScoring
          ? t('{{first}} first · {{repeat}} each additional', {
            first: formatNumber(pledge.points, 0),
            repeat: formatNumber(repeatPointsForPledge(pledge), 0)
          })
          : formatNumber(pledge.points, 0)
        }
      </div>
    </div>
    {hasRepeatScoring && nextPoints > 0 &&
      <div className="cc__stats__item">
        <div className="cc__stats__label">{t('Next completion')}</div>
        <div className="cc__lighttext">{formatNumber(nextPoints, 0)} {t('points')}</div>
      </div>
    }
    {(isCcsf && Boolean(pledge.savingsCO2Lbs)) &&
      <div className="cc__stats__item">
        <div
          className="cc__stats__label"
          dangerouslySetInnerHTML={{
            __html: t('Pounds CO<sub>2</sub> savings')
          }}
        />
        <div className="cc__lighttext">
          {formatNumber(pledge.savingsCO2Lbs, 0)}
          <div className="cc__lighttext" style={{ fontSize: '0.85em' }}>{t('per completion')}</div>
        </div>
      </div>
    }
    {(isCcsf && waterGallons > 0) &&
      <div className="cc__stats__item">
        <div className="cc__stats__label">{t('Water savings (gal)')}</div>
        <div className="cc__lighttext">
          {formatNumber(waterGallons, 0)}
          <div className="cc__lighttext" style={{ fontSize: '0.85em' }}>{t('per completion')}</div>
        </div>
      </div>
    }
    <div className="cc__stats__item">
      <div className="cc__stats__label">{t('Completions')}</div>
      <div className="cc__lighttext">{formatNumber(completions, 0)} / {pledge.repeats}</div>
    </div>
    {Boolean(pointsEarned) &&
      <div className="cc__stats__item">
        <div className="cc__stats__label">{t('Points Earned')}</div>
        <div className="cc__lighttext">{formatNumber(pointsEarned, 0)}</div>
      </div>
    }
    {(Boolean(pledge.savingsCO2Lbs) || waterGallons > 0) &&
      <div className="cc__stats__item">
        <div className="cc__stats__label">{t('Savings details')}</div>
        <div>
          <Link to={makeActionSavingsPath()}>{t('Interactive savings table')}</Link>
          {' · '}
          <Link to={makeActionSavingsPath('methodology')}>{t('Methodology')}</Link>
        </div>
      </div>
    }
  </div>
  );
};

export default compose(
  withLocalization('userStats')
)(PledgeStats) as React.FunctionComponent<Props>;
